import {
  applyEdits,
  type ArtifactDto, type Block, type ChatMessageDto, type ConversationDto, type EditChange, type EditEvent, type EditOp, type MeDto,
} from '@ads/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api';
import { keys, uploadAttachment, useArtifact, useConversation, useSession } from './queries';
import { streamEvents } from './sse';

/* ─────────────────────────────────────────────────────────────────────
   Uma rodada da conversa, vista do navegador: os anexos sobem, o pedido
   sai, e as operações do agente chegam uma a uma e se aplicam sobre a
   versão de partida — o canvas muda enquanto o agente escreve. No fim,
   a peça em cache vira a versão nova, sem recarregar nada.
   ───────────────────────────────────────────────────────────────────── */

export interface PendingFile { key: string; file: File; preview: string | null }

export interface LiveTurn {
  request: ChatMessageDto | null;
  reply: ChatMessageDto | null;
  /** o que está sendo enviado antes do pedido sair */
  uploading: PendingFile[];
  text: string;
  phase: string;
  changes: EditChange[];
  base: Block[] | null;
  ops: EditOp[];
}

const idle: LiveTurn = { request: null, reply: null, uploading: [], text: '', phase: '', changes: [], base: null, ops: [] };

export function useArtifactChat(artifactId: string | null | undefined) {
  const { projectId } = useSession();
  const qc = useQueryClient();
  const artifact = useArtifact(artifactId ?? undefined);
  const conv = useConversation(artifactId);
  const [live, setLive] = useState<LiveTurn | null>(null);
  const [fresh, setFresh] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);
  /* a rodada vive numa ref, atualizada no instante do evento: operação e "done" podem chegar
     no mesmo pacote do stream, antes de o React desenhar a operação */
  const liveRef = useRef<LiveTurn | null>(null);
  const commit = useCallback((next: LiveTurn | null) => { liveRef.current = next; setLive(next); }, []);
  /* miniaturas locais dos anexos: valem até o servidor confirmar o pedido */
  const previews = useRef<string[]>([]);
  const dropPreviews = () => { for (const u of previews.current.splice(0)) URL.revokeObjectURL(u); };

  /* a peça que o canvas mostra: a versão de partida com as operações que já chegaram */
  const applied = useMemo(() => (live?.base ? applyEdits(live.base, live.ops) : null), [live?.base, live?.ops]);
  const blocks = applied?.blocks ?? artifact.data?.body ?? null;
  const shownFresh = applied ? applied.fresh : fresh;

  const finish = useCallback((reply: ChatMessageDto, request: ChatMessageDto | null, extra: { revision?: ConversationDto['revisions'][number] | null; title?: string }) => {
    const turn = liveRef.current;
    const result = turn?.base ? applyEdits(turn.base, turn.ops) : null;
    if (artifactId && result && turn!.ops.length) {
      qc.setQueryData<ArtifactDto>(keys.artifact(artifactId), a => (a ? {
        ...a, body: result.blocks, blocks: result.blocks.length, title: extra.title ?? a.title, revision: extra.revision?.n ?? a.revision,
      } : a));
      setFresh(result.fresh);
    } else if (artifactId && extra.revision) {
      /* o fim chegou sem as operações (reconexão depois do fim): a peça vem do servidor */
      void qc.invalidateQueries({ queryKey: keys.artifact(artifactId) });
    }
    if (artifactId) {
      qc.setQueryData<ConversationDto>(keys.conversation(artifactId), c => (c ? {
        ...c,
        messages: [...c.messages.filter(m => m.id !== reply.id && m.id !== request?.id), ...(request ? [request] : []), reply],
        revisions: extra.revision ? [extra.revision, ...c.revisions.filter(r => r.id !== extra.revision!.id)] : c.revisions,
        head: extra.revision?.n ?? c.head,
        running: null,
      } : c));
      void qc.invalidateQueries({ queryKey: keys.conversation(artifactId) });
      void qc.invalidateQueries({ queryKey: keys.artifacts(projectId) });
    }
    commit(null);
  }, [artifactId, qc, projectId, commit]);

  const onEvent = useCallback((e: EditEvent) => {
    switch (e.event) {
      case 'start': {
        dropPreviews();
        const cached = artifactId ? qc.getQueryData<ArtifactDto>(keys.artifact(artifactId)) : undefined;
        const l = liveRef.current;
        setFresh([]);
        commit({ ...(l ?? idle), uploading: [], request: e.data.request, reply: e.data.reply, base: cached?.body ?? l?.base ?? [], ops: [], changes: [] });
        return;
      }
      case 'phase': if (liveRef.current) commit({ ...liveRef.current, phase: e.data.label }); return;
      case 'reply': if (liveRef.current) commit({ ...liveRef.current, text: e.data.text }); return;
      case 'op': {
        const l = liveRef.current;
        if (l && e.data.index >= l.ops.length) commit({ ...l, ops: [...l.ops, e.data.op], changes: [...l.changes, e.data.change] });
        return;
      }
      case 'usage': return;
      case 'done':
        if (e.data.credits.quota) qc.setQueryData<MeDto>(keys.me, me => (me ? { ...me, org: { ...me.org, credits: e.data.credits } } : me));
        finish(e.data.reply, liveRef.current?.request ?? null, { revision: e.data.revision, title: e.data.title });
        return;
      case 'error': {
        const reply = liveRef.current?.reply;
        setError(e.data.message);
        void qc.invalidateQueries({ queryKey: keys.me });
        if (reply) finish({ ...reply, status: 'failed', error: e.data.message, text: liveRef.current?.text ?? '' }, liveRef.current?.request ?? null, {});
        else commit(null);
      }
    }
  }, [artifactId, qc, finish, commit]);

  const stream = useCallback(async (url: string, method: 'GET' | 'POST', body?: unknown) => {
    abort.current?.abort();
    const ctrl = new AbortController();
    abort.current = ctrl;
    try {
      await streamEvents<EditEvent>(url, { method, body, signal: ctrl.signal, onEvent });
    } catch (err) {
      if (ctrl.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'A conexão caiu.');
      commit(null);
      if (artifactId) void qc.invalidateQueries({ queryKey: keys.conversation(artifactId) });
    }
  }, [onEvent, artifactId, qc, commit]);

  /** Envia um pedido, com fotos e arquivos. */
  const send = useCallback(async (text: string, files: File[]) => {
    if (!artifactId || liveRef.current) return false;
    setError(null);
    const uploading = files.map((file, i) => ({ key: `${file.name}-${file.size}-${i}`, file, preview: /^image\/(png|jpeg|webp|gif)$/.test(file.type) ? URL.createObjectURL(file) : null }));
    previews.current.push(...uploading.flatMap(u => (u.preview ? [u.preview] : [])));
    commit({ ...idle, uploading, text: '', phase: files.length ? `Enviando ${files.length === 1 ? 'o anexo' : `${files.length} anexos`}` : 'Enviando' });
    let ids: string[];
    try {
      ids = (await Promise.all(files.map(f => uploadAttachment(projectId, artifactId, f)))).map(m => m.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'O envio falhou.');
      commit(null);
      dropPreviews();
      return false;
    }
    void stream(`/artifacts/${artifactId}/messages`, 'POST', { text, attachments: ids });
    return true;
  }, [artifactId, projectId, stream, commit]);

  /* voltou para a peça com uma rodada em curso: reconecta */
  const resumed = useRef<string | null>(null);
  const running = conv.data?.running ?? null;
  useEffect(() => {
    if (!artifactId || !running || running === 'pendente' || liveRef.current || resumed.current === running) return;
    resumed.current = running;
    commit({ ...idle, phase: 'Reconectando' });
    void stream(`/artifacts/${artifactId}/messages/${running}/stream`, 'GET');
  }, [artifactId, running, stream, commit]);

  const cancel = useCallback(async () => {
    const id = liveRef.current?.reply?.id;
    if (artifactId && id) await api(`/artifacts/${artifactId}/messages/${id}/cancel`, { method: 'POST' }).catch(() => {});
  }, [artifactId]);

  /** Volta a peça a uma versão (que vira a mais nova). */
  const restore = useCallback(async (revisionId: string) => {
    if (!artifactId || liveRef.current) return;
    setError(null);
    try {
      const r = await api<{ n: number; title: string; blocks: Block[]; conversation: ConversationDto }>(`/artifacts/${artifactId}/revisions/${revisionId}/restore`, { method: 'POST' });
      qc.setQueryData<ArtifactDto>(keys.artifact(artifactId), a => (a ? { ...a, body: r.blocks, blocks: r.blocks.length, title: r.title, revision: r.n } : a));
      qc.setQueryData(keys.conversation(artifactId), r.conversation);
      setFresh([]);
      void qc.invalidateQueries({ queryKey: keys.artifacts(projectId) });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível voltar a esta versão.');
    }
  }, [artifactId, qc, projectId]);

  useEffect(() => () => { abort.current?.abort(); dropPreviews(); }, []);

  return {
    artifact: artifact.data ?? null,
    conversation: conv.data ?? null,
    blocks,
    fresh: shownFresh,
    title: applied?.title ?? artifact.data?.title ?? null,
    live,
    busy: !!live,
    error,
    clearError: () => setError(null),
    send, cancel, restore,
  };
}

export type ArtifactChat = ReturnType<typeof useArtifactChat>;
